import { Flex, Input, Text } from "@chakra-ui/react";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import AddImageIcon from "./icons/AddImageIcon";

interface FileInputProps {
	name: string;
	display?: string;
	maxNumberOfFiles?: number;
	onFileChange?: (files: File[] | null) => void;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	accept?: string;
}

const FileInput = forwardRef<HTMLInputElement | undefined, FileInputProps>(
	(
		{ name, maxNumberOfFiles, onFileChange, onChange, onBlur, display, accept },
		ref,
	) => {
		const [files, setFiles] = useState<File[]>([]);
		const inputRef = useRef<HTMLInputElement>(null);
		useImperativeHandle(ref, () => inputRef.current!);

		const handleOnInput = useCallback(() => {
			if (inputRef.current?.files) {
				const currentFiles = Array.from(inputRef.current.files).slice(
					0,
					maxNumberOfFiles ?? Number.POSITIVE_INFINITY,
				);
				if (currentFiles.length === 0 && files.length !== 0) {
					return;
				}
				if (onFileChange) {
					onFileChange(currentFiles);
				}
				setFiles(currentFiles);
			} else {
				// onFileChange(null);
			}
		}, [onFileChange, maxNumberOfFiles, files.length]);

		const handleOnChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				if (onChange) {
					onChange(event);
				}
			},
			[onChange],
		);

		useEffect(() => {}, [inputRef.current?.files]);

		return (
			<Flex
				display={display}
				bg="brand.50"
				border="dashed 1px"
				borderColor="brand.700"
				borderRadius="lg"
				justifyContent={"center"}
				direction={"column"}
				alignItems={"center"}
				py={6}
				w={"100%"}
				onClick={() => {
					if (inputRef.current) {
						inputRef.current.click();
					}
				}}
				onDragOver={(event) => {
					event.stopPropagation();
					event.preventDefault();
				}}
				onDrop={(event) => {
					event.preventDefault();
					if (inputRef.current) {
						inputRef.current.files = event.dataTransfer.files;
						setFiles(
							Array.from(inputRef.current.files).slice(
								0,
								maxNumberOfFiles ?? Number.POSITIVE_INFINITY,
							),
						);
						if (onFileChange) {
							onFileChange(
								Array.from(inputRef.current.files).slice(
									0,
									maxNumberOfFiles ?? Number.POSITIVE_INFINITY,
								),
							);
						}
					}
				}}
			>
				<Input
					ref={inputRef}
					type="file"
					name={name}
					display={"none"}
					onInput={handleOnInput}
					onChange={handleOnChange}
					onBlur={onBlur}
					accept={accept}
				></Input>

				<br />
				<AddImageIcon color="brand.700" w={"24px"} h={"24px"}></AddImageIcon>
				<Text
					color="gray.500"
					fontSize={"14px"}
					fontWeight={500}
					lineHeight={"21px"}
				>
					Fichier
				</Text>
				{Array.isArray(files) &&
					files.map((file) => <Text key={file.name}>{file.name}</Text>)}
				<Text
					color="brand.700"
					fontSize={"14px"}
					fontWeight={600}
					lineHeight={"24px"}
				>
					Cliquez ici ou glissez le fichier à importer
				</Text>
			</Flex>
		);
	},
);

export default FileInput;
